import { Component } from '@theme/component';

// Pairing suggestions for the existing library. Unknown future entries remain in All.
// Generic unpublished meal ideas work with either base; this does not mark them published.
const pairings = [
  ['Classic Breakfast Arepa', 'classic'],
  ['Chócolo with Butter & Crumbled Cheese', 'chocolo'],
  ['Ham & Cheese Arepa', 'either'],
  ['Avocado & Egg Arepa', 'either'],
  ['Chicken & Vegetable Arepa', 'either'],
  ['Shredded Beef & Cheese Arepa', 'either'],
  ['Mushroom & Cheese Arepa', 'either'],
  ['Steak, Peppers & Cheese Arepa', 'either'],
  ['Chicken, Vegetables & Sauce Arepa', 'either'],
  ['Avocado & Cheese Arepa', 'either'],
  ['Bacon & Butter Arepa', 'either'],
  ['Chócolo with Nutella & Cheese', 'chocolo'],
  ['Chócolo with Sweetened Condensed Milk & Cheese', 'chocolo'],
  ['Hawaiian Arepizza with Ham & Pineapple', 'classic'],
  ['Classic Butter & Cheese Arepa', 'classic'],
];

class TdaRecipeLibrary extends Component {
  meal = 'all';
  arepa = 'all';
  groups = [];

  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }

  initialize() {
    this.groups = [];
    for (const meal of ['breakfast', 'lunch', 'dinner', 'snacks']) {
      const heading = this.querySelector(`#tda-${meal}`);
      const description = heading?.nextElementSibling;
      const list = description?.nextElementSibling;
      if (!heading || !description || list?.tagName !== 'UL') continue;
      const rows = Array.from(list.children);
      for (const row of rows) {
        const match = pairings.find(([title]) => row.textContent.includes(title));
        row.dataset.recipe = match?.[1] || 'unclassified';
        if (match && !row.querySelector('.tda-pairing')) {
          const badge = document.createElement('span');
          badge.className = 'tda-pairing';
          badge.textContent = this.dataset[`${match[1]}Label`];
          row.append(badge);
        }
      }
      this.groups.push({ meal, heading, description, list, rows });
    }
    if (this.groups.length !== 4) return; // Leave the source usable if its structure changes.
    for (const element of this.querySelectorAll('[data-enhanced],button[data-meal]')) element.hidden = false;
    for (const link of this.querySelectorAll('a[data-meal]')) {
      link.setAttribute('role', 'button');
      link.setAttribute('on:keydown', '/keyFilter');
    }
    this.updateResults();
  }

  /** @param {KeyboardEvent} event */
  keyFilter(event) {
    if (event.key === ' ') this.filter(event);
  }

  /** @param {Event} event */
  filter(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement) || this.groups.length !== 4) return;
    event.preventDefault();
    if (target.dataset.meal) this.meal = target.dataset.meal;
    if (target.dataset.arepa) this.arepa = target.dataset.arepa;
    this.updateResults();
    this.showResults();
  }

  reset() {
    this.meal = 'all';
    this.arepa = 'all';
    this.updateResults();
    this.showResults();
  }

  // Move both viewport and keyboard focus to the first visible result, not on initial load.
  showResults() {
    const firstGroup = this.groups.find((group) => !group.heading.hidden);
    const destination = firstGroup?.heading || this.querySelector('[data-empty]');
    if (!(destination instanceof HTMLElement)) return;
    destination.setAttribute('tabindex', '-1');
    destination.focus({ preventScroll: true });
    destination.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      block: 'start',
    });
  }

  updateResults() {
    let count = 0;
    for (const group of this.groups) {
      let visible = 0;
      for (const row of group.rows) {
        const mealMatch = this.meal === 'all' || this.meal === group.meal;
        const arepaMatch = this.arepa === 'all' || row.dataset.recipe === this.arepa ||
          (this.arepa !== 'either' && row.dataset.recipe === 'either');
        row.hidden = !(mealMatch && arepaMatch);
        if (!row.hidden) visible++;
      }
      for (const element of [group.heading, group.description, group.list]) element.hidden = visible === 0;
      count += visible;
    }
    for (const control of this.querySelectorAll('[data-meal]')) control.setAttribute('aria-pressed', String(control.dataset.meal === this.meal));
    for (const control of this.querySelectorAll('[data-arepa]')) control.setAttribute('aria-pressed', String(control.dataset.arepa === this.arepa));
    const status = this.querySelector('[data-status]');
    if (status) status.textContent = `${count} ${this.dataset.resultsLabel}`;
    const empty = this.querySelector('[data-empty]');
    if (empty) empty.hidden = count !== 0;
  }
}

if (!customElements.get('tda-recipe-library')) customElements.define('tda-recipe-library', TdaRecipeLibrary);
